import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import { MenuItem } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import { useQuill } from '../../editor/QuillContext';
import trashIcon from '../../icons/trash.svg';
import MagneticWallItemOverflowMenu from './MagneticWallItemOverflowMenu';
import styles from './ReactionThreadFull.module.scss';
import ThreadReactionList from './ThreadReactionList';
import { useAddReaction, useDeleteReaction } from './reactionsMutations';
import useDeleteReactionThread from './useDeleteReactionThread';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const THREAD_FRAGMENT = gql(`
  fragment ReactionThreadFull_thread on PublicThreadFields {
    id
    author {
      id
    }
    comments {
      id
    }
    reactions {
      id
      ...ThreadReactionList_reactions
    }
  }
`);

interface ReactionThreadFullProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  isExpanded: boolean;
  isHovered: boolean;
  withinOverlay?: boolean;
}

const ReactionThreadFull = ({
  thread,
  isExpanded,
  isHovered,
  withinOverlay,
}: ReactionThreadFullProps) => {
  const addReaction = useAddReaction();
  const deleteReaction = useDeleteReaction();
  const deleteReactionThread = useDeleteReactionThread();

  const currentUser = useAppSelector((state) => state.session.user);

  const { quill } = useQuill();

  const handleAddReaction = (emoji: string) => {
    addReaction(emoji, thread.id);
  };

  const handleRemoveReaction = (id: string) => {
    deleteReaction(id, thread, quill);
  };

  const handleDeleteAllReactions = () => {
    deleteReactionThread(thread.id);
  };

  const { reactions } = thread;

  const canDeleteReactionThread = thread.author?.id === currentUser?.id;

  return (
    <div
      className={classNames(styles.container, {
        [styles.expanded]: isExpanded,
      })}
    >
      <AnimatePresence initial={false}>
        {isExpanded && isHovered && canDeleteReactionThread && (
          <OpacityMotionTransition>
            <MagneticWallItemOverflowMenu
              menuItems={[
                <MenuItem
                  icon={trashIcon}
                  key="delete"
                  danger
                  onClick={handleDeleteAllReactions}
                >
                  Delete thread
                </MenuItem>,
              ]}
            />
          </OpacityMotionTransition>
        )}
      </AnimatePresence>
      <div className={styles.reactions}>
        <ThreadReactionList
          expanded={isExpanded}
          reactions={reactions}
          onAdd={handleAddReaction}
          onRemove={handleRemoveReaction}
          withinOverlay={withinOverlay}
        />
      </div>
    </div>
  );
};

export default ReactionThreadFull;
